import React from 'react'
import { Button, Element, Message } from 'react-bulma-components'
import { PreBillingsFilters, MissionsFilters } from '../../store/filters'
import DescriptionList from '../description-list/description-list'
import { getWeek, getWeekYear, localDate } from '../../utils/date'
import { useNavigate } from 'react-router-dom'
import { formatCost } from '../../utils/finance'
import WorkerLabel from '../texts/worker-label'
import {
  useCreateThreePartiesMutation,
  useSimulateThreePartiesPeriodSummaryQuery,
} from '../../queries/period-summaries'

export interface CreatePreBillingProps {
  interimAgencyId?: string
  service?: MissionsFilters['service']
  jobTitle?: MissionsFilters['jobTitle']
  start: PreBillingsFilters['start']
  end: PreBillingsFilters['end']
}

const CreatePreBilling: React.FC<CreatePreBillingProps> = ({
  interimAgencyId,
  jobTitle,
  service,
  start,
}) => {
  // Pre billing mutation
  const mutation = useCreateThreePartiesMutation()
  const navigate = useNavigate()

  const newPreBillingParams = {
    week: getWeek(new Date(start as Date)),
    year: getWeekYear(new Date(start as Date)),
    interimAgency: interimAgencyId!,
    jobTitle,
    service,
  }

  const simulateQuery = useSimulateThreePartiesPeriodSummaryQuery(newPreBillingParams, {
    enabled: Boolean(interimAgencyId),
  })

  React.useEffect(() => {
    if (mutation.isSuccess) navigate(`/pre-billings/${mutation.data._id}`)
  }, [mutation.isSuccess])

  if (simulateQuery.isLoading || !simulateQuery.data) return <Element>Chargement en cours</Element>

  return (
    <Element>
      {simulateQuery.data?.missions.readyForSummary === 0 && (
        <Message color="danger">
          <Message.Body>
            <Element renderAs="p" textWeight="bold">
              Pas de Mission éligible pour la Pré-Facturation
            </Element>
          </Message.Body>
        </Message>
      )}
      {simulateQuery.data?.missions.count !== simulateQuery.data?.missions.readyForSummary && (
        <Message color="warning">
          <Message.Body>
            <Element renderAs="p" textWeight="bold" mb={3}>
              {simulateQuery.data?.missions.notReadyForSummary} Mission(s) non prises en compte
            </Element>
            <Element className="content">
              <Element renderAs="ul" textSize={7}>
                <Element renderAs="li">
                  Uniquement les missions validées sont prise en compte
                </Element>
                <Element renderAs="li">
                  Toutes les missions pour un même <WorkerLabel /> doivent être pré-facturées en
                  même temps
                </Element>
                <Element renderAs="li">
                  Uniquement les missions correspondantes aux filtres sélectionnés sont prise en
                  compte
                </Element>
                <Element renderAs="li">
                  Les missions déjà pré-facturées ne sont pas prise en compte
                </Element>
              </Element>
            </Element>
          </Message.Body>
        </Message>
      )}
      <DescriptionList
        items={[
          {
            label: 'Missions concernées',
            value: `${simulateQuery.data?.missions.readyForSummary} / ${simulateQuery.data?.missions.count}`,
          },
          { label: 'Du', value: localDate(simulateQuery.data.from) },
          { label: 'Au', value: localDate(simulateQuery.data.to) },
          { label: 'Agence', value: simulateQuery.data.interimAgencyName },
          {
            label: 'Métier',
            value: simulateQuery.data.jobTitles?.[0]
              ? simulateQuery.data.jobTitles.join(', ')
              : 'Tous',
          },
          {
            label: 'Affectation',
            value: simulateQuery.data.services?.[0]
              ? simulateQuery.data.services.join(', ')
              : 'Tous',
          },
          { label: 'Montant total', value: formatCost(simulateQuery.data.totals?.costs?.total) },
        ]}
      />
      <Button
        fullwidth
        color={'success'}
        data-test="create-pre-billing-submit"
        onClick={() => {
          if (simulateQuery.data.missions.readyForSummary >= 1) {
            mutation.mutate(newPreBillingParams)
          }
        }}
        disabled={
          !simulateQuery.data?.missions.readyForSummary || mutation.isLoading || mutation.isSuccess
        }
      >
        Générer la pré-facturation de{' '}
        {`${simulateQuery.data?.missions.readyForSummary} / ${simulateQuery.data?.missions.count}`}{' '}
        Mission(s)
      </Button>
      <Message color="info" mt={5} mb={3}>
        <Message.Body>
          <Element renderAs="p">
            Une pré-facturation peut être annulée ultérieurement mais uniquement via une demande
            d'annulation et avec l'accord de l'autre partie.
          </Element>
        </Message.Body>
      </Message>
    </Element>
  )
}

export default CreatePreBilling
